import { useSuspenseQuery } from '@apollo/client';
import { QUERY_ONBOARDING } from '@edwin-group/people-graphql';
import useMarkStepDone from './useMarkStepDone';
import { useNavigate } from 'react-router-dom';
import { onCompletedOnBoarding } from '@/services/on-boarding/onboarding.service';
import { getAuthentication, useAuthentication } from '@/hooks/useAuthentication';
import { trackGAEvent } from '@/lib/ga-custom-events/dispatcher';

export interface OnBoardingStepData {
  title: string;
  description?: string | null;
  image?: {
    url: string;
    alternativeText?: string | null;
  } | null;
  button?: {
    text: string;
    link: string;
  } | null;
}

export interface OnBoardingPageData {
  logo?: {
    url: string;
    alternativeText?: string | null;
  } | null;
  steps?: (OnBoardingStepData | null)[] | null;
}

export interface OnBoardingQueryResult {
  onBoardingPage?: OnBoardingPageData | null;
}

export const useOnBoardingV5 = (step: number) => {
  const { data: onBoardingData, error } = useSuspenseQuery<OnBoardingQueryResult>(QUERY_ONBOARDING, {
    fetchPolicy: 'cache-first',
  });
  const navigate = useNavigate();

  const { markStepDone } = useMarkStepDone();
  const { toggleOnboardingStatus } = useAuthentication();

  const steps = onBoardingData?.onBoardingPage?.steps ?? [];
  const totalSteps = steps.length;
  const currentStep = steps[step - 1];
  const isLastStep = step >= totalSteps;

  const completeStep = async (url?: string) => {
    const nextUrl = url ?? currentStep?.button?.link ?? `/on-boarding/step/${step + 1}`;
    markStepDone(nextUrl);

    if (isLastStep || nextUrl === '/') {
      toggleOnboardingStatus('onboardHomeCompleted');
      window.location.href = '/';
      return;
    }

    navigate(nextUrl);
  };

  const onClickOnBoardingCompleted = async () => {
    const { user } = getAuthentication();
    if (!user) return;
    trackGAEvent('onboarding_completed', {});
    await onCompletedOnBoarding(user.id!);
    completeStep('/');
  };

  return {
    page: onBoardingData.onBoardingPage,
    steps,
    totalSteps,
    step,
    currentStep,
    isLastStep,
    error,
    completeStep,
    onClickOnBoardingCompleted,
  };
};
